class VariantSelects extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.onVariantChange.bind(this));
    this.variants = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
  }

  onVariantChange() {
    const selectedOptions = [];
    const optionGroups = new Map();

    this.querySelectorAll('[data-option-position]').forEach((input) => {
      const position = Number(input.dataset.optionPosition);
      if (!optionGroups.has(position)) optionGroups.set(position, []);
      optionGroups.get(position).push(input);
    });

    Array.from(optionGroups.keys())
      .sort((a, b) => a - b)
      .forEach((position) => {
        const inputs = optionGroups.get(position);
        const checkedInput = inputs.find((input) => input.checked);
        const select = inputs.find((input) => input.tagName === 'SELECT');
        selectedOptions.push(checkedInput?.value || select?.value);
      });

    const variant = this.variants.find((candidate) => {
      return candidate.options.every((option, index) => option === selectedOptions[index]);
    });

    const form = this.closest('form');
    const idInput = form.querySelector('[data-variant-id]');
    const addButton = form.querySelector('[name="add"]');
    const addText = form.querySelector('[data-add-to-cart-text]');

    if (!variant) {
      addButton.disabled = true;
      addText.textContent = window.theme.strings.unavailable;
      return;
    }

    idInput.value = variant.id;
    addButton.disabled = !variant.available;
    addText.textContent = variant.available ? window.theme.strings.addToCart : window.theme.strings.soldOut;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }
}

customElements.define('variant-selects', VariantSelects);

const selectors = {
  cartDrawer: '[data-cart-drawer]',
  cartOpen: '[data-cart-open]',
  cartClose: '[data-cart-close]',
  cartCount: '[data-cart-count]',
  cartQuantityLink: '[data-cart-quantity-link]',
  collectionSidebar: '[data-collection-sidebar]',
  collectionFilterToggle: '[data-collection-filter-toggle]',
  collectionFilterClose: '[data-collection-filter-close]',
  announcement: '[data-rotating-announcement]',
  countdownTimer: '[data-countdown-timer]',
  featuredCarousel: '[data-featured-carousel]',
  igCarousel: '[data-ig-carousel]',
  instagramGallery: '[data-instagram-gallery]',
  mobileMenu: '[data-mobile-menu]',
  menuToggle: '[data-menu-toggle]',
  menuClose: '[data-menu-close]',
  productForm: '[data-product-form]',
  productBundleCarousel: '[data-product-bundle-carousel]',
  productGallery: '[data-product-gallery]',
  popup: '[data-newsletter-popup]',
  quantityStepper: '[data-quantity-stepper]',
  reviewToggle: '[data-review-toggle]',
  reviewForm: '[data-review-form]',
  salesPopup: '[data-sales-popup]',
  scrollRevealBanner: '[data-scroll-reveal-banner]',
  shareButton: '[data-share-button]',
  sortSelect: '[data-sort-select]',
  testimonials: '[data-testimonials]',
  copyCode: '[data-copy-code]',
  tabbedProductRow: '[data-tabbed-product-row]',
  wishlistDrawer: '[data-wishlist-drawer]',
  wishlistOpen: '[data-wishlist-open]',
  wishlistClose: '[data-wishlist-close]',
  wishlistToggle: '[data-wishlist-toggle]',
  wishlistItems: '[data-wishlist-items]',
  wishlistEmpty: '[data-wishlist-empty]',
  wishlistCount: '[data-wishlist-count]'
};

function setupBrowserCompatibilityClasses() {
  const userAgent = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isSafari = /Safari/.test(userAgent) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(userAgent);
  const isAlternateIOSBrowser = isIOS && !isSafari;

  if (isIOS) {
    document.documentElement.classList.add('is-ios-browser');
  }

  if (isAlternateIOSBrowser) {
    document.documentElement.classList.add('is-ios-alt-browser');
  }

  if (isIOS && navigator.brave?.isBrave) {
    navigator.brave.isBrave().then((isBrave) => {
      if (!isBrave) return;
      document.documentElement.classList.add('is-ios-alt-browser');
      setupNewsletterSafeFields();
      setupAltBrowserFormSubmissions();
      setupPasswordSafeFields();
    }).catch(() => {});
  }
}

setupBrowserCompatibilityClasses();

function openHiddenElement(element) {
  if (!element) return;
  element.hidden = false;
  element.setAttribute('aria-hidden', 'false');
  document.body.style.overflow = 'hidden';
  if (element.matches?.(selectors.mobileMenu)) {
    document.documentElement.classList.add('mobile-menu-is-open');
  }
}

function closeHiddenElement(element) {
  if (!element) return;
  element.hidden = true;
  element.setAttribute('aria-hidden', 'true');
  document.body.style.overflow = '';
  if (element.matches?.(selectors.mobileMenu)) {
    document.documentElement.classList.remove('mobile-menu-is-open');
  }
}

function setupMobileMenu() {
  const menu = document.querySelector(selectors.mobileMenu);
  const toggles = document.querySelectorAll(selectors.menuToggle);
  const close = document.querySelector(selectors.menuClose);

  if (menu && menu.parentElement !== document.body) {
    document.body.appendChild(menu);
  }

  toggles.forEach((toggle) => toggle.addEventListener('click', () => {
    openHiddenElement(menu);
    toggles.forEach((button) => button.setAttribute('aria-expanded', 'true'));
  }));

  close?.addEventListener('click', () => {
    closeHiddenElement(menu);
    toggles.forEach((button) => button.setAttribute('aria-expanded', 'false'));
  });

  menu?.addEventListener('click', (event) => {
    if (event.target === menu) {
      closeHiddenElement(menu);
      toggles.forEach((button) => button.setAttribute('aria-expanded', 'false'));
    }
  });
}

function setupCartDrawer() {
  const drawer = document.querySelector(selectors.cartDrawer);
  const openButtons = document.querySelectorAll(selectors.cartOpen);
  const closeButtons = document.querySelectorAll(selectors.cartClose);

  openButtons.forEach((button) => {
    button.addEventListener('click', (event) => {
      if (window.matchMedia('(max-width: 749px)').matches) return;
      if (!drawer) return;
      event.preventDefault();
      openHiddenElement(drawer);
    });
  });

  closeButtons.forEach((button) => {
    button.addEventListener('click', () => closeHiddenElement(drawer));
  });
}

function setupWishlist() {
  const storageKey = 'atlas:wishlist';
  const drawer = document.querySelector(selectors.wishlistDrawer);

  const readItems = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
      return Array.isArray(parsed) ? parsed.filter((item) => item && item.handle) : [];
    } catch (error) {
      return [];
    }
  };

  const saveItems = (items) => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(items));
    } catch (error) {
      // Browsers can block localStorage in private contexts. The UI still behaves for this visit.
    }
  };

  const escapeHtml = (value) =>
    String(value || '').replace(/[&<>"']/g, (character) => {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      }[character];
    });

  const updateCounts = (items) => {
    document.querySelectorAll(selectors.wishlistCount).forEach((counter) => {
      counter.textContent = items.length;
      counter.hidden = items.length === 0;
    });
  };

  const updateToggles = (items) => {
    document.querySelectorAll(selectors.wishlistToggle).forEach((button) => {
      const isSaved = items.some((item) => item.handle === button.dataset.productHandle);
      button.classList.toggle('is-active', isSaved);
      button.setAttribute('aria-pressed', isSaved ? 'true' : 'false');
      const label = button.querySelector('[data-wishlist-label]');
      if (label) label.textContent = isSaved ? 'Saved to wishlist' : 'Save to wishlist';
    });
  };

  const renderItems = (items) => {
    const container = drawer?.querySelector(selectors.wishlistItems);
    const empty = drawer?.querySelector(selectors.wishlistEmpty);
    if (!container || !empty) return;

    empty.hidden = items.length > 0;
    container.innerHTML = items
      .map((item) => {
        const image = item.image
          ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy">`
          : '<div class="wishlist-item__placeholder" aria-hidden="true"></div>';
        return `
          <article class="wishlist-item">
            <a class="wishlist-item__image" href="${escapeHtml(item.url)}">${image}</a>
            <div class="wishlist-item__content">
              <a href="${escapeHtml(item.url)}">${escapeHtml(item.title)}</a>
              <span>${escapeHtml(item.price)}</span>
              <button type="button" data-wishlist-remove="${escapeHtml(item.handle)}">Remove</button>
            </div>
          </article>
        `;
      })
      .join('');
  };

  const refresh = () => {
    const items = readItems();
    updateCounts(items);
    updateToggles(items);
    renderItems(items);
    return items;
  };

  document.querySelectorAll(selectors.wishlistOpen).forEach((button) => {
    button.addEventListener('click', () => {
      refresh();
      openHiddenElement(drawer);
    });
  });

  document.querySelectorAll(selectors.wishlistClose).forEach((button) => {
    button.addEventListener('click', () => closeHiddenElement(drawer));
  });

  drawer?.addEventListener('click', (event) => {
    if (event.target.matches(selectors.wishlistClose)) closeHiddenElement(drawer);
    const remove = event.target.closest('[data-wishlist-remove]');
    if (!remove) return;
    const items = readItems().filter((item) => item.handle !== remove.dataset.wishlistRemove);
    saveItems(items);
    refresh();
  });

  document.querySelectorAll(selectors.wishlistToggle).forEach((button) => {
    button.addEventListener('click', () => {
      button.classList.remove('is-animating');
      void button.offsetWidth;
      button.classList.add('is-animating');
      window.setTimeout(() => button.classList.remove('is-animating'), 700);

      const product = {
        handle: button.dataset.productHandle,
        title: button.dataset.productTitle,
        url: button.dataset.productUrl,
        image: button.dataset.productImage,
        price: button.dataset.productPrice
      };
      if (!product.handle) return;

      const items = readItems();
      const exists = items.some((item) => item.handle === product.handle);
      const nextItems = exists ? items.filter((item) => item.handle !== product.handle) : [product, ...items];
      saveItems(nextItems.slice(0, 24));
      refresh();
    });
  });

  refresh();
}

function applyDiscountCode(discountCode) {
  const code = String(discountCode || '').trim();
  if (!code) return false;
  const root = window.Shopify?.routes?.root || '/';
  const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
  window.location.href = `${normalizedRoot}discount/${encodeURIComponent(code)}?redirect=/checkout`;
  return true;
}

function setupCartDiscounts() {
  document.addEventListener('click', (event) => {
    const applyButton = event.target.closest('[data-apply-discount]');
    if (!applyButton) return;
    const box = applyButton.closest('[data-discount-box]');
    const input = box?.querySelector('[data-discount-input]');
    if (input && applyDiscountCode(input.value)) event.preventDefault();
  });

  document.addEventListener('click', (event) => {
    const checkoutButton = event.target.closest('[data-checkout-button]');
    if (!checkoutButton) return;
    const form = checkoutButton.closest('form');
    const input = form?.querySelector('[data-discount-input]');
    if (input && input.value.trim() && applyDiscountCode(input.value)) event.preventDefault();
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!event.submitter?.matches?.('[data-checkout-button]')) return;
    const input = form.querySelector('[data-discount-input]');
    if (input && input.value.trim() && applyDiscountCode(input.value)) event.preventDefault();
  });
}

function setupCartQuantityLinks() {
  if (window.__xsCartQuantityLinksInstalled) return;
  window.__xsCartQuantityLinksInstalled = true;

  document.addEventListener('click', async (event) => {
    const link = event.target.closest(selectors.cartQuantityLink);
    if (!link) return;

    event.preventDefault();
    if (link.dataset.loading === 'true') return;
    link.dataset.loading = 'true';

    const line = Number(link.dataset.cartLine);
    const key = link.dataset.cartKey;
    const quantity = Number(link.dataset.cartQuantity);
    const cartUrl = window.theme?.routes?.cartUrl || '/cart';
    const updateRoute = window.theme?.routes?.cartUpdate || '/cart/update';
    const updateUrl = updateRoute.endsWith('.js') ? updateRoute : `${updateRoute}.js`;

    if (!key || Number.isNaN(quantity)) {
      window.location.href = cartUrl;
      return;
    }

    try {
      const body = new URLSearchParams();
      body.set(`updates[${key}]`, String(quantity));

      const response = await fetch(updateUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
        },
        body
      });

      if (!response.ok) throw new Error('Unable to update cart quantity');
      const cart = await response.json();
      const changedItem = cart.items?.find((item) => item.key === key);
      if (quantity > 0 && (!changedItem || changedItem.quantity !== quantity)) {
        throw new Error('Cart quantity did not change');
      }
      window.location.href = `${cartUrl}${cartUrl.includes('?') ? '&' : '?'}_=${Date.now()}`;
    } catch (error) {
      const fallback = new URL(link.href, window.location.origin);
      fallback.searchParams.set('return_to', cartUrl);
      window.location.href = fallback.toString();
    }
  });
}

async function refreshCartCount() {
  const response = await fetch(`${window.theme.routes.cartUrl}.js`);
  const cart = await response.json();
  document.querySelectorAll(selectors.cartCount).forEach((counter) => {
    counter.textContent = cart.item_count;
  });
  return cart;
}

function setupProductForms() {
  document.querySelectorAll(selectors.productForm).forEach((form) => {
    form.addEventListener('submit', async (event) => {
      const drawer = document.querySelector(selectors.cartDrawer);
      if (window.matchMedia('(max-width: 749px)').matches) return;
      if (!drawer || !window.theme) return;

      event.preventDefault();
      const button = form.querySelector('[name="add"]');
      button.disabled = true;

      try {
        const response = await fetch(window.theme.routes.cartAdd, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        });

        if (!response.ok) throw new Error('Unable to add item to cart');

        const cart = await refreshCartCount();
        const drawerBody = drawer.querySelector('[data-cart-drawer-body]');
        if (drawerBody) {
          drawerBody.innerHTML = `
            <div class="cart-drawer__added">
              <div class="cart-drawer__added-mark" aria-hidden="true">
                <svg viewBox="0 0 24 24"><path d="m5 12 4 4L19 6" /></svg>
              </div>
              <p class="eyebrow">Added to your cart</p>
              <h3>Reserved in cart</h3>
              <p>Your selection is ready. Review the full cart or move straight to secure checkout.</p>
              <div class="cart-drawer__added-summary">
                <span>${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}</span>
                <strong>${window.Shopify && Shopify.formatMoney ? Shopify.formatMoney(cart.total_price) : ''}</strong>
              </div>
              <div class="cart-drawer__added-actions">
                <a class="button button--full" href="${window.theme.routes.cartUrl}">View cart</a>
                <a class="button button--secondary button--full" href="/checkout">Checkout</a>
              </div>
            </div>
          `;
        }
        openHiddenElement(drawer);
      } catch (error) {
        window.location.href = window.theme.routes.cartUrl;
      } finally {
        button.disabled = false;
      }
    });
  });
}

function setupSortSelects() {
  document.querySelectorAll(selectors.sortSelect).forEach((select) => {
    select.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      window.location.href = url.toString();
    });
  });
}

function setupCollectionFilters() {
  const sidebar = document.querySelector(selectors.collectionSidebar);
  const toggle = document.querySelector(selectors.collectionFilterToggle);
  const close = document.querySelector(selectors.collectionFilterClose);
  if (!sidebar || !toggle) return;

  toggle.addEventListener('click', () => {
    sidebar.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  });

  close?.addEventListener('click', () => {
    sidebar.classList.remove('is-open');
    document.body.style.overflow = '';
  });
}

function rotateItems(containerSelector, itemSelector, fallbackInterval = 5000) {
  document.querySelectorAll(containerSelector).forEach((container) => {
    const items = Array.from(container.querySelectorAll(itemSelector));
    if (items.length < 2) return;
    const interval = Number(container.dataset.interval || fallbackInterval);
    let index = 0;
    setInterval(() => {
      items[index].classList.remove('is-active');
      index = (index + 1) % items.length;
      items[index].classList.add('is-active');
    }, interval);
  });
}

function setupNewsletterPopup() {
  const popup = document.querySelector(selectors.popup);
  if (!popup) return;
  const storageKey = 'atlasNewsletterPopupClosed';
  if (localStorage.getItem(storageKey) === 'true') return;
  const delay = Number(popup.dataset.delay || 5000);

  setTimeout(() => openHiddenElement(popup), delay);
  popup.querySelectorAll('[data-popup-close]').forEach((button) => {
    button.addEventListener('click', () => {
      localStorage.setItem(storageKey, 'true');
      closeHiddenElement(popup);
    });
  });
}

function setupSalesPopup() {
  const popup = document.querySelector(selectors.salesPopup);
  if (!popup) return;

  const closeButton = popup.querySelector('[data-sales-close]');
  const items = Array.from(popup.querySelectorAll('[data-sales-item]'));
  const delay = Number(popup.dataset.salesDelay || 3) * 1000;
  const duration = Number(popup.dataset.salesDuration || 8) * 1000;
  const repeatDelay = Number(popup.dataset.salesRepeat || 45) * 1000;
  let activeIndex = -1;
  let showTimer;
  let hideTimer;
  let repeatTimer;

  const showNextItem = () => {
    if (!items.length) return;
    activeIndex = (activeIndex + 1) % items.length;
    items.forEach((item, index) => {
      item.hidden = index !== activeIndex;
    });
  };

  const hidePopup = (repeat = true) => {
    popup.classList.remove('is-visible', 'is-animating');
    window.clearTimeout(hideTimer);

    window.setTimeout(() => {
      popup.hidden = true;
    }, 240);

    if (repeat) {
      repeatTimer = window.setTimeout(showPopup, repeatDelay);
    }
  };

  const showPopup = () => {
    showNextItem();
    popup.hidden = false;
    popup.classList.remove('is-visible', 'is-animating');
    void popup.offsetWidth;
    window.requestAnimationFrame(() => {
      popup.classList.add('is-visible', 'is-animating');
      window.setTimeout(() => popup.classList.remove('is-animating'), 2600);
    });
    hideTimer = window.setTimeout(() => hidePopup(true), duration);
  };

  closeButton?.addEventListener('click', () => {
    window.clearTimeout(showTimer);
    window.clearTimeout(hideTimer);
    window.clearTimeout(repeatTimer);
    hidePopup(false);
  });

  showTimer = window.setTimeout(showPopup, delay);
}

function setupProductGalleries() {
  document.querySelectorAll(selectors.productGallery).forEach((gallery) => {
    const mediaItems = gallery.querySelectorAll('[data-product-media]');
    const thumbs = gallery.querySelectorAll('[data-media-thumb]');
    const thumbsTrack = gallery.querySelector('.product__thumbs');
    const thumbsPrev = gallery.querySelector('[data-product-thumbs-prev]');
    const thumbsNext = gallery.querySelector('[data-product-thumbs-next]');

    const updateThumbAlignment = () => {
      if (!thumbsTrack) return;
      const showArrows = thumbs.length > 1;
      thumbsTrack.classList.toggle('product__thumbs--centered', !showArrows);
      if (thumbsPrev && thumbsNext) {
        thumbsPrev.hidden = !showArrows;
        thumbsNext.hidden = !showArrows;
        thumbsPrev.disabled = false;
        thumbsNext.disabled = false;
      }
    };

    const scrollThumbs = (direction) => {
      if (!thumbsTrack) return;
      const maxScroll = Math.max(thumbsTrack.scrollWidth - thumbsTrack.clientWidth, 0);
      if (maxScroll <= 2) {
        const activeThumb = gallery.querySelector('[data-media-thumb].is-active');
        const activeIndex = Array.from(thumbs).indexOf(activeThumb);
        const nextIndex = (activeIndex + direction + thumbs.length) % thumbs.length;
        thumbs[nextIndex]?.click();
        return;
      }
      if (direction < 0 && thumbsTrack.scrollLeft <= 2) {
        thumbsTrack.scrollTo({ left: maxScroll, behavior: 'smooth' });
        window.setTimeout(updateThumbAlignment, 280);
        return;
      }
      if (direction > 0 && thumbsTrack.scrollLeft >= maxScroll - 2) {
        thumbsTrack.scrollTo({ left: 0, behavior: 'smooth' });
        window.setTimeout(updateThumbAlignment, 280);
        return;
      }
      thumbsTrack.scrollBy({
        left: direction * Math.max(thumbsTrack.clientWidth * 0.75, 96),
        behavior: 'smooth',
      });
      window.setTimeout(updateThumbAlignment, 280);
    };

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const mediaId = thumb.dataset.mediaThumb;
        let matchedMedia = false;
        mediaItems.forEach((media) => {
          const isSelected = media.dataset.mediaId === mediaId;
          matchedMedia = matchedMedia || isSelected;
          media.classList.toggle('product__media--hidden', !isSelected);
          media.hidden = !isSelected;
          if (isSelected) {
            media.querySelectorAll('img').forEach((image) => {
              image.loading = 'eager';
              image.fetchPriority = 'high';
            });
          }
        });
        if (!matchedMedia && mediaItems[0]) {
          mediaItems.forEach((media, index) => {
            const isSelected = index === 0;
            media.classList.toggle('product__media--hidden', !isSelected);
            media.hidden = !isSelected;
          });
        }
        thumbs.forEach((candidate) => candidate.classList.toggle('is-active', candidate === thumb));
        thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        window.setTimeout(updateThumbAlignment, 280);
      });
    });

    thumbsPrev?.addEventListener('click', () => scrollThumbs(-1));
    thumbsNext?.addEventListener('click', () => scrollThumbs(1));
    thumbsTrack?.addEventListener('scroll', updateThumbAlignment, { passive: true });
    updateThumbAlignment();
    window.addEventListener('resize', updateThumbAlignment, { passive: true });
    thumbsTrack?.querySelectorAll('img').forEach((image) => {
      if (image.complete) return;
      image.addEventListener('load', updateThumbAlignment, { once: true });
    });
  });
}

function setupQuantitySteppers() {
  document.querySelectorAll(selectors.quantityStepper).forEach((stepper) => {
    const input = stepper.querySelector('input[type="number"]');
    const minus = stepper.querySelector('[data-quantity-minus]');
    const plus = stepper.querySelector('[data-quantity-plus]');

    minus?.addEventListener('click', () => {
      const min = Number(input.min || 1);
      input.value = Math.max(min, Number(input.value || min) - 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    plus?.addEventListener('click', () => {
      input.value = Number(input.value || 1) + 1;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });
}

function setupShareButtons() {
  document.querySelectorAll(selectors.shareButton).forEach((button) => {
    button.addEventListener('click', async () => {
      const url = button.dataset.shareUrl || window.location.href;
      if (navigator.share) {
        await navigator.share({ title: document.title, url });
        return;
      }

      if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        const originalText = button.textContent;
        button.textContent = 'Link copied';
        setTimeout(() => {
          button.textContent = originalText;
        }, 1800);
      }
    });
  });
}

function setupCopyCodeButtons() {
  document.querySelectorAll(selectors.copyCode).forEach((button) => {
    button.addEventListener('click', async () => {
      const code = button.dataset.copyCode;
      if (!code || !navigator.clipboard) return;
      await navigator.clipboard.writeText(code);
      const label = button.querySelector('span');
      if (!label) return;
      const originalText = label.textContent;
      label.textContent = 'Copied';
      setTimeout(() => {
        label.textContent = originalText;
      }, 1600);
    });
  });
}

function setupPromptBackedFields() {
  document.querySelectorAll('.contact-form').forEach((form) => {
    const nameInput = form.querySelector('[data-contact-name-input]');
    const hiddenName = form.querySelector('[data-contact-name-hidden]');
    const promptButton = form.querySelector('[data-contact-name-prompt]');
    const emailInput = form.querySelector('[data-contact-email-input]');
    const hiddenEmail = form.querySelector('[data-contact-email-hidden]');
    const emailPromptButton = form.querySelector('[data-contact-email-prompt]');
    const phoneInput = form.querySelector('[data-contact-phone-input]');
    const hiddenPhone = form.querySelector('[data-contact-phone-hidden]');
    const phonePromptButton = form.querySelector('[data-contact-phone-prompt]');
    const messageInput = form.querySelector('[data-contact-message-input]');
    const hiddenMessage = form.querySelector('[data-contact-message-hidden]');
    const messagePromptButton = form.querySelector('[data-contact-message-prompt]');
    const localMessage = form.querySelector('[data-contact-local-message]');
    const submitButton = form.querySelector('button[type="submit"]');
    if (submitButton && !submitButton.dataset.originalText) submitButton.dataset.originalText = submitButton.textContent;

    const setName = (value) => {
      const nextValue = String(value || '').trim();
      if (hiddenName) hiddenName.value = nextValue;
      if (nameInput) nameInput.value = nextValue;
      if (promptButton) {
        promptButton.textContent = nextValue || promptButton.dataset.placeholder || 'Your name';
        promptButton.classList.toggle('has-value', Boolean(nextValue));
      }
    };

    const setEmail = (value) => {
      const nextValue = String(value || '').trim();
      if (hiddenEmail) hiddenEmail.value = nextValue;
      if (emailInput) emailInput.value = nextValue;
      if (emailPromptButton) {
        emailPromptButton.textContent = nextValue || emailPromptButton.dataset.placeholder || 'Email';
        emailPromptButton.classList.toggle('has-value', Boolean(nextValue));
      }
    };

    const setPhone = (value) => {
      const nextValue = String(value || '').trim();
      if (hiddenPhone) hiddenPhone.value = nextValue;
      if (phoneInput) phoneInput.value = nextValue;
      if (phonePromptButton) {
        phonePromptButton.textContent = nextValue || phonePromptButton.dataset.placeholder || 'Phone';
        phonePromptButton.classList.toggle('has-value', Boolean(nextValue));
      }
    };

    const setMessage = (value) => {
      const nextValue = String(value || '').trim();
      if (hiddenMessage) hiddenMessage.value = nextValue;
      if (messageInput) messageInput.value = nextValue;
      if (messagePromptButton) {
        messagePromptButton.textContent = nextValue || messagePromptButton.dataset.placeholder || 'Message';
        messagePromptButton.classList.toggle('has-value', Boolean(nextValue));
      }
    };

    setName(hiddenName?.value || nameInput?.value || '');
    setEmail(hiddenEmail?.value || emailInput?.value || '');
    setPhone(hiddenPhone?.value || phoneInput?.value || '');
    setMessage(hiddenMessage?.value || messageInput?.value || '');

    nameInput?.addEventListener('input', () => setName(nameInput.value));
    emailInput?.addEventListener('input', () => setEmail(emailInput.value));
    phoneInput?.addEventListener('input', () => setPhone(phoneInput.value));
    messageInput?.addEventListener('input', () => setMessage(messageInput.value));
    promptButton?.addEventListener('click', () => {
      const nextValue = window.prompt(promptButton.dataset.placeholder || 'Your name', hiddenName?.value || '');
      if (nextValue !== null) setName(nextValue);
    });
    emailPromptButton?.addEventListener('click', () => {
      const nextValue = window.prompt(emailPromptButton.dataset.placeholder || 'Email', hiddenEmail?.value || '');
      if (nextValue !== null) setEmail(nextValue);
    });
    phonePromptButton?.addEventListener('click', () => {
      const nextValue = window.prompt(phonePromptButton.dataset.placeholder || 'Phone', hiddenPhone?.value || '');
      if (nextValue !== null) setPhone(nextValue);
    });
    messagePromptButton?.addEventListener('click', () => {
      const nextValue = window.prompt(messagePromptButton.dataset.placeholder || 'Message', hiddenMessage?.value || '');
      if (nextValue !== null) setMessage(nextValue);
    });

    const syncContactForm = (event) => {
      setName(nameInput?.value || hiddenName?.value || '');
      setEmail(emailInput?.value || hiddenEmail?.value || '');
      setPhone(phoneInput?.value || hiddenPhone?.value || '');
      setMessage(messageInput?.value || hiddenMessage?.value || '');

      if (hiddenEmail && !hiddenEmail.value) {
        const nextValue = window.prompt(emailPromptButton?.dataset.placeholder || 'Email', '');
        if (nextValue === null || !nextValue.trim()) {
          event.preventDefault();
          return;
        }
        setEmail(nextValue);
      }

      if (hiddenMessage && !hiddenMessage.value) {
        const nextValue = window.prompt(messagePromptButton?.dataset.placeholder || 'Message', '');
        if (nextValue === null || !nextValue.trim()) {
          event.preventDefault();
          return;
        }
        setMessage(nextValue);
      }
    };

    form.addEventListener('submit', syncContactForm);
  });

  document.querySelectorAll('.search-form-mobile').forEach((form) => {
    const searchInput = form.querySelector('[data-mobile-search-input]');
    const hiddenSearch = form.querySelector('[data-mobile-search-hidden]');
    const promptButton = form.querySelector('[data-mobile-search-prompt]');

    const setSearch = (value) => {
      const nextValue = String(value || '').trim();
      if (hiddenSearch) hiddenSearch.value = nextValue;
      if (searchInput) searchInput.value = nextValue;
      if (promptButton) {
        promptButton.textContent = nextValue || promptButton.dataset.placeholder || 'Search';
        promptButton.classList.toggle('has-value', Boolean(nextValue));
      }
    };

    setSearch(hiddenSearch?.value || searchInput?.value || '');

    searchInput?.addEventListener('input', () => setSearch(searchInput.value));
    promptButton?.addEventListener('click', () => {
      const nextValue = window.prompt(promptButton.dataset.placeholder || 'Search', hiddenSearch?.value || '');
      if (nextValue !== null) setSearch(nextValue);
    });

    form.addEventListener('submit', (event) => {
      setSearch(searchInput?.value || hiddenSearch?.value || '');
      if (hiddenSearch?.value) return;
      const nextValue = window.prompt(promptButton?.dataset.placeholder || 'Search', '');
      if (nextValue === null || !nextValue.trim()) {
        event.preventDefault();
        return;
      }
      setSearch(nextValue);
    });
  });
}

function setupNewsletterSafeFields() {
  document.querySelectorAll('.newsletter-form input[name="contact[email]"]').forEach((emailInput) => {
    emailInput.readOnly = false;
    emailInput.removeAttribute('readonly');
    emailInput.setAttribute('inputmode', 'email');
  });

  document.querySelectorAll('.newsletter-form').forEach((form) => {
    const visibleEmail = form.querySelector('[data-newsletter-email-input]');
    const hiddenEmail = form.querySelector('[data-newsletter-email-hidden]');
    const promptButton = form.querySelector('[data-newsletter-email-prompt]');
    if (!visibleEmail || !hiddenEmail || !promptButton) return;

    const setEmail = (value) => {
      const nextValue = String(value || '').trim();
      visibleEmail.value = nextValue;
      hiddenEmail.value = nextValue;
      promptButton.textContent = nextValue || promptButton.dataset.placeholder || 'Email address';
      promptButton.classList.toggle('has-value', Boolean(nextValue));
    };

    setEmail(hiddenEmail.value || visibleEmail.value || '');
    visibleEmail.addEventListener('input', () => setEmail(visibleEmail.value));
    promptButton.addEventListener('click', () => {
      const nextValue = window.prompt(promptButton.dataset.placeholder || 'Email address', hiddenEmail.value || '');
      if (nextValue !== null) setEmail(nextValue);
    });
  });
}

function setupAltBrowserFormSubmissions() {
  if (window.__xsInlineFormSafetyInstalled) return;

  const showFormMessage = (form, message, isError = false) => {
    let messageElement = form.querySelector('[data-contact-local-message]') || form.querySelector('[data-review-local-message]') || form.querySelector('[data-ajax-form-message]');

    if (!messageElement) {
      messageElement = document.createElement('p');
      messageElement.className = 'form-message';
      messageElement.dataset.ajaxFormMessage = 'true';
      form.appendChild(messageElement);
    }

    messageElement.hidden = false;
    messageElement.textContent = message;
    messageElement.classList.toggle('form-message--error', isError);
  };

  const resetPromptBackedFields = (form) => {
    form.querySelectorAll('input[type="hidden"][name^="contact["]').forEach((input) => {
      if (input.name !== 'contact[tags]') input.value = '';
    });
    form.querySelectorAll('[data-contact-name-input], [data-contact-email-input], [data-contact-phone-input], [data-contact-message-input], [data-review-name-input], [data-review-email-input], [data-review-title-input], [data-review-body-input]').forEach((field) => {
      field.value = '';
    });
    form.querySelectorAll('[data-contact-name-prompt], [data-contact-email-prompt], [data-contact-phone-prompt], [data-contact-message-prompt], [data-review-name-prompt], [data-review-email-prompt], [data-review-title-prompt], [data-review-body-prompt]').forEach((button) => {
      button.textContent = button.dataset.placeholder || '';
      button.classList.remove('has-value');
    });
  };

  const syncReviewForm = (form) => {
    const nameInput = form.querySelector('[data-review-name-input]');
    const nameHidden = form.querySelector('[data-review-name-hidden]');
    const emailInput = form.querySelector('[data-review-email-input]');
    const emailHidden = form.querySelector('[data-review-email-hidden]');
    const titleInput = form.querySelector('[data-review-title-input]');
    const titleHidden = form.querySelector('[data-review-title-hidden]');
    const bodyInput = form.querySelector('[data-review-body-input]');
    const bodyHidden = form.querySelector('[data-review-body-hidden]');
    if (nameInput && nameHidden) nameHidden.value = String(nameInput.value || '').trim();
    if (emailInput && emailHidden) emailHidden.value = String(emailInput.value || '').trim();
    if (titleInput && titleHidden) titleHidden.value = String(titleInput.value || '').trim();
    if (bodyInput && bodyHidden) bodyHidden.value = String(bodyInput.value || '').trim();
  };

  const submitFormInHiddenFrame = (form, done) => {
    const frameName = `xs-form-target-${Date.now()}-${Math.round(Math.random() * 100000)}`;
    const frame = document.createElement('iframe');
    let finished = false;

    frame.name = frameName;
    frame.title = 'Form submission';
    frame.hidden = true;
    frame.style.display = 'none';
    document.body.appendChild(frame);

    const originalTarget = form.getAttribute('target');
    form.setAttribute('target', frameName);

    const finish = () => {
      if (finished) return;
      finished = true;
      if (originalTarget === null) {
        form.removeAttribute('target');
      } else {
        form.setAttribute('target', originalTarget);
      }
      done();
      setTimeout(() => frame.remove(), 1200);
    };

    frame.addEventListener('load', finish, { once: true });
    setTimeout(finish, 1800);
    HTMLFormElement.prototype.submit.call(form);
  };

  document.querySelectorAll('.contact-form, .review-form').forEach((form) => {
    if (form.dataset.ajaxSafeSubmit === 'true') return;
    form.dataset.ajaxSafeSubmit = 'true';

    form.addEventListener('submit', (event) => {
      if (event.defaultPrevented) return;

      if (form.matches('.review-form')) syncReviewForm(form);

      if (!form.checkValidity()) {
        event.preventDefault();
        form.reportValidity?.();
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      const submitButton = form.querySelector('button[type="submit"]');
      const originalLabel = submitButton?.textContent || '';

      if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = 'Sending...';
      }

      submitFormInHiddenFrame(form, () => {
        if (form.matches('.review-form')) {
          showFormMessage(form, 'Thanks for your review. It will be checked before publishing.');
          resetPromptBackedFields(form);
        } else {
          showFormMessage(form, 'Thanks for contacting us. We will get back to you soon.');
          resetPromptBackedFields(form);
        }

        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent = originalLabel;
        }
      });
    });
  });
}

function setupPasswordSafeFields() {
  if (!document.documentElement.classList.contains('is-ios-alt-browser')) return;

  document.querySelectorAll('form[action*="/password"] input[name="password"], .xs-password-card input[name="password"]').forEach((passwordInput) => {
    if (passwordInput.dataset.passwordSafeField === 'true') return;

    passwordInput.dataset.passwordSafeField = 'true';
    passwordInput.readOnly = true;
    passwordInput.setAttribute('inputmode', 'none');

    const openPrompt = () => {
      const label = passwordInput.getAttribute('placeholder') || 'Store password';
      const nextValue = window.prompt(label, passwordInput.value || '');
      if (nextValue !== null) {
        passwordInput.value = String(nextValue || '').trim();
        passwordInput.dispatchEvent(new Event('input', { bubbles: true }));
        passwordInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };

    const openWithoutFocus = (event) => {
      event.preventDefault();
      event.stopPropagation();
      passwordInput.blur();
      openPrompt();
    };

    passwordInput.addEventListener('pointerdown', openWithoutFocus);
    passwordInput.addEventListener('touchstart', openWithoutFocus, { passive: false });
    passwordInput.addEventListener('mousedown', openWithoutFocus);
    passwordInput.addEventListener('focus', (event) => {
      event.preventDefault();
      passwordInput.blur();
    });
    passwordInput.addEventListener('click', openWithoutFocus);
  });
}

function setupDesktopOnlyVideos() {
  const desktopQuery = window.matchMedia('(min-width: 750px)');

  document.querySelectorAll('[data-desktop-video]').forEach((video) => {
    const sources = Array.from(video.querySelectorAll('source[data-src]'));
    if (!sources.length) return;

    const syncVideo = () => {
      if (!desktopQuery.matches) {
        video.pause();
        if (video.dataset.desktopVideoLoaded === 'true') {
          sources.forEach((source) => source.removeAttribute('src'));
          video.removeAttribute('src');
          video.load();
          video.dataset.desktopVideoLoaded = 'false';
        }
        return;
      }

      if (video.dataset.desktopVideoLoaded !== 'true') {
        sources.forEach((source) => {
          source.setAttribute('src', source.dataset.src);
        });
        video.load();
        video.dataset.desktopVideoLoaded = 'true';
      }

      if (video.hasAttribute('autoplay')) {
        video.play().catch(() => {});
      }
    };

    if (video.dataset.desktopVideoBound !== 'true') {
      video.dataset.desktopVideoBound = 'true';
      desktopQuery.addEventListener?.('change', syncVideo);
    }

    syncVideo();
  });
}

function setupHalfCountdownVideos() {
  const desktopQuery = window.matchMedia('(min-width: 750px)');

  document.querySelectorAll('[data-half-countdown-video-template]').forEach((template) => {
    const section = template.closest('[data-half-countdown-section]');
    const media = template.closest('.half-countdown-banner__media');
    if (!section || !media) return;

    const shouldLoadVideo = desktopQuery.matches;
    const existingWrap = media.querySelector('[data-half-countdown-video-wrap]');
    const poster = media.querySelector('.half-countdown-banner__poster');

    if (!shouldLoadVideo) {
      if (existingWrap) {
        const video = existingWrap.querySelector('video');
        if (video) video.pause();
        existingWrap.remove();
      }
      if (poster) poster.hidden = false;
      return;
    }

    if (existingWrap) {
      const video = existingWrap.querySelector('video');
      if (video && video.hasAttribute('autoplay')) video.play().catch(() => {});
      return;
    }

    const templateNode = template.content && template.content.firstElementChild;
    const wrap = templateNode ? templateNode.cloneNode(true) : null;
    if (!wrap) return;
    wrap.setAttribute('data-half-countdown-video-wrap', '');
    media.insertBefore(wrap, template);

    const video = wrap.querySelector('video');
    if (!video) return;
    if (poster) poster.hidden = true;
    if (video.hasAttribute('autoplay')) {
      video.play().catch(() => {
        wrap.remove();
        if (poster) poster.hidden = false;
      });
    }
  });

  if (window.halfCountdownVideoResizeBound !== true) {
    window.halfCountdownVideoResizeBound = true;
    desktopQuery.addEventListener?.('change', setupHalfCountdownVideos);
  }
}

function setupCountdownTimers() {
  document.querySelectorAll(selectors.countdownTimer).forEach((timer) => {
    if (timer.dataset.countdownInit === 'true') return;
    const values = {
      days: timer.querySelector('[data-countdown-days]'),
      hours: timer.querySelector('[data-countdown-hours]'),
      minutes: timer.querySelector('[data-countdown-minutes]'),
      seconds: timer.querySelector('[data-countdown-seconds]')
    };

    if (!values.days || !values.hours || !values.minutes || !values.seconds) return;

    const initialSeconds =
      Number(timer.dataset.days || 0) * 86400 +
      Number(timer.dataset.hours || 0) * 3600 +
      Number(timer.dataset.minutes || 0) * 60 +
      Number(timer.dataset.seconds || 0);

    if (!initialSeconds) return;
    timer.dataset.countdownInit = 'true';
    let remaining = initialSeconds;

    const render = () => {
      const days = Math.floor(remaining / 86400);
      const hours = Math.floor((remaining % 86400) / 3600);
      const minutes = Math.floor((remaining % 3600) / 60);
      const seconds = remaining % 60;

      values.days.textContent = String(days).padStart(2, '0');
      values.hours.textContent = String(hours).padStart(2, '0');
      values.minutes.textContent = String(minutes).padStart(2, '0');
      values.seconds.textContent = String(seconds).padStart(2, '0');
    };

    render();
    setInterval(() => {
      remaining = remaining <= 0 ? initialSeconds : remaining - 1;
      render();
    }, 1000);
  });
}

function setupIgCarousels() {
  document.querySelectorAll(selectors.igCarousel).forEach((carousel) => {
    if (carousel.dataset.igCarouselInit === 'true') return;
    const track = carousel.querySelector('[data-ig-carousel-track]');
    if (!track) return;

    carousel.dataset.igCarouselInit = 'true';
    if (window.Shopify?.designMode) {
      carousel.classList.add('ig-carousel--editor-preview');
    }
    const videos = Array.from(track.querySelectorAll('video'));
    const firstGroup = track.querySelector('.ig-carousel__group');
    const desktopVideoQuery = window.matchMedia('(min-width: 750px)');

    const isVideoNearViewport = (video) => {
      if (!desktopVideoQuery.matches) return true;
      const card = video.closest('.ig-card') || video;
      const cardRect = card.getBoundingClientRect();
      const trackRect = track.getBoundingClientRect();
      const buffer = Math.max(track.clientWidth, 320);
      return cardRect.right >= trackRect.left - buffer && cardRect.left <= trackRect.right + buffer;
    };

    const prepareVideo = (video) => {
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.loop = true;
      video.autoplay = true;
      video.preload = 'auto';
      video.controls = false;
      video.removeAttribute('controls');
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('loop', '');
      video.setAttribute('autoplay', '');
    };

    const playVideoWhenReady = (video) => {
      prepareVideo(video);
      if (!isVideoNearViewport(video)) return;
      if (document.hidden || !video.paused) return;
      if (video.readyState >= 2) {
        video.play().catch(() => {});
        return;
      }
      if (video.dataset.igPlayPending === 'true') return;
      video.dataset.igPlayPending = 'true';
      video.addEventListener('canplay', () => {
        delete video.dataset.igPlayPending;
        if (!document.hidden) video.play().catch(() => {});
      }, { once: true, passive: true });
    };

    const keepVideosPlaying = () => {
      videos.forEach((video) => {
        if (isVideoNearViewport(video)) {
          video.preload = 'auto';
          playVideoWhenReady(video);
        } else {
          prepareVideo(video);
          video.preload = 'metadata';
        }
      });
    };

    const bindVideoKeepAlive = () => {
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) keepVideosPlaying();
      });

      ['pointerdown', 'touchstart', 'click'].forEach((eventName) => {
        document.addEventListener(eventName, keepVideosPlaying, { passive: true });
      });

      videos.forEach((video) => {
        prepareVideo(video);
        video.addEventListener('loadedmetadata', () => playVideoWhenReady(video), { passive: true });
        video.addEventListener('canplay', () => playVideoWhenReady(video), { passive: true });
      });

      videos.forEach((video, index) => {
        window.setTimeout(() => {
          if (isVideoNearViewport(video)) playVideoWhenReady(video);
        }, Math.min(index * 120, 1200));
      });
      window.setTimeout(keepVideosPlaying, 1500);
      window.setInterval(keepVideosPlaying, 5000);
    };

    bindVideoKeepAlive();

    if (carousel.dataset.autoplay !== 'true' || !firstGroup) return;

    const speedSeconds = Math.max(3, Number(carousel.dataset.speed || 28));
    let frameId = 0;
    let lastTimestamp = 0;
    let scrollPosition = track.scrollLeft;
    let lastVideoCheck = 0;

    const getGap = (element) => {
      const styles = window.getComputedStyle(element);
      return parseFloat(styles.columnGap || styles.gap || '0') || 0;
    };

    const getCardStep = () => {
      const card = firstGroup.querySelector('.ig-card');
      if (!card) return Math.max(1, track.clientWidth);
      return card.getBoundingClientRect().width + getGap(firstGroup);
    };

    const getLoopWidth = () => {
      const groups = Array.from(track.querySelectorAll('.ig-carousel__group'));
      if (groups.length > 1) {
        const distance = groups[1].offsetLeft - groups[0].offsetLeft;
        if (distance > 0) return distance;
      }
      return Math.max(1, firstGroup.scrollWidth + getGap(track));
    };

    const metrics = {
      cardStep: Math.max(1, track.clientWidth),
      loopWidth: Math.max(1, firstGroup.scrollWidth + getGap(track))
    };

    const measure = () => {
      metrics.cardStep = Math.max(1, getCardStep());
      metrics.loopWidth = Math.max(1, getLoopWidth());
      if (scrollPosition >= metrics.loopWidth) {
        scrollPosition %= metrics.loopWidth;
        track.scrollLeft = scrollPosition;
      }
    };

    const tick = (timestamp) => {
      if (!lastTimestamp) lastTimestamp = timestamp;
      const delta = timestamp - lastTimestamp;
      lastTimestamp = timestamp;

      if (!lastVideoCheck || timestamp - lastVideoCheck > 2500) {
        lastVideoCheck = timestamp;
        keepVideosPlaying();
      }

      const pixelsPerSecond = metrics.cardStep / speedSeconds;
      scrollPosition += (pixelsPerSecond * delta) / 1000;

      if (metrics.loopWidth > 1 && scrollPosition >= metrics.loopWidth) {
        scrollPosition %= metrics.loopWidth;
      }
      track.scrollLeft = scrollPosition;

      frameId = window.requestAnimationFrame(tick);
    };

    const stop = () => {
      window.cancelAnimationFrame(frameId);
      frameId = 0;
      lastTimestamp = 0;
    };

    const start = () => {
      stop();
      measure();
      keepVideosPlaying();
      frameId = window.requestAnimationFrame(tick);
    };

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        stop();
      } else {
        scrollPosition = track.scrollLeft;
        start();
      }
    });

    const syncManualScroll = () => {
      scrollPosition = track.scrollLeft;
      if (metrics.loopWidth > 1 && scrollPosition >= metrics.loopWidth) {
        scrollPosition %= metrics.loopWidth;
        track.scrollLeft = scrollPosition;
      }
    };

    window.addEventListener('resize', measure, { passive: true });
    track.querySelectorAll('img').forEach((image) => {
      if (!image.complete) image.addEventListener('load', measure, { once: true, passive: true });
    });
    videos.forEach((video) => {
      video.addEventListener('loadedmetadata', measure, { passive: true });
    });

    measure();
    track.addEventListener('pointerdown', syncManualScroll, { passive: true });
    track.addEventListener('touchstart', syncManualScroll, { passive: true });
    track.addEventListener('wheel', syncManualScroll, { passive: true });

    start();
  });
}

function setupReviewForms() {
  document.querySelectorAll(selectors.reviewToggle).forEach((button) => {
    button.addEventListener('click', () => {
      const section = button.closest('[data-review-section]');
      const form = section?.querySelector(selectors.reviewForm);
      if (!form) return;
      form.hidden = !form.hidden;
      if (!form.hidden) form.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  });

  document.querySelectorAll('.review-form').forEach((form) => {
    const nameInput = form.querySelector('[data-review-name-input]');
    const nameHidden = form.querySelector('[data-review-name-hidden]');
    const namePrompt = form.querySelector('[data-review-name-prompt]');
    const emailInput = form.querySelector('[data-review-email-input]');
    const emailHidden = form.querySelector('[data-review-email-hidden]');
    const emailPrompt = form.querySelector('[data-review-email-prompt]');
    const titleInput = form.querySelector('[data-review-title-input]');
    const titleHidden = form.querySelector('[data-review-title-hidden]');
    const titlePrompt = form.querySelector('[data-review-title-prompt]');
    const bodyInput = form.querySelector('[data-review-body-input]');
    const bodyHidden = form.querySelector('[data-review-body-hidden]');
    const bodyPrompt = form.querySelector('[data-review-body-prompt]');

    const setPromptValue = (input, hidden, prompt, value) => {
      const nextValue = String(value || '').trim();
      if (input) input.value = nextValue;
      if (hidden) hidden.value = nextValue;
      if (prompt) {
        prompt.textContent = nextValue || prompt.dataset.placeholder || '';
        prompt.classList.toggle('has-value', Boolean(nextValue));
      }
    };

    setPromptValue(nameInput, nameHidden, namePrompt, nameHidden?.value || nameInput?.value || '');
    setPromptValue(emailInput, emailHidden, emailPrompt, emailHidden?.value || emailInput?.value || '');
    setPromptValue(titleInput, titleHidden, titlePrompt, titleHidden?.value || titleInput?.value || '');
    setPromptValue(bodyInput, bodyHidden, bodyPrompt, bodyHidden?.value || bodyInput?.value || '');

    nameInput?.addEventListener('input', () => setPromptValue(nameInput, nameHidden, namePrompt, nameInput.value));
    emailInput?.addEventListener('input', () => setPromptValue(emailInput, emailHidden, emailPrompt, emailInput.value));
    titleInput?.addEventListener('input', () => setPromptValue(titleInput, titleHidden, titlePrompt, titleInput.value));
    bodyInput?.addEventListener('input', () => setPromptValue(bodyInput, bodyHidden, bodyPrompt, bodyInput.value));

    namePrompt?.addEventListener('click', () => {
      const nextValue = window.prompt(namePrompt.dataset.placeholder || 'Your name', nameHidden?.value || '');
      if (nextValue !== null) setPromptValue(nameInput, nameHidden, namePrompt, nextValue);
    });

    emailPrompt?.addEventListener('click', () => {
      const nextValue = window.prompt(emailPrompt.dataset.placeholder || 'Email', emailHidden?.value || '');
      if (nextValue !== null) setPromptValue(emailInput, emailHidden, emailPrompt, nextValue);
    });

    titlePrompt?.addEventListener('click', () => {
      const nextValue = window.prompt(titlePrompt.dataset.placeholder || 'Review title', titleHidden?.value || '');
      if (nextValue !== null) setPromptValue(titleInput, titleHidden, titlePrompt, nextValue);
    });

    bodyPrompt?.addEventListener('click', () => {
      const nextValue = window.prompt(bodyPrompt.dataset.placeholder || 'Review', bodyHidden?.value || '');
      if (nextValue !== null) setPromptValue(bodyInput, bodyHidden, bodyPrompt, nextValue);
    });
  });
}

function setupFeaturedCarousels() {
  document.querySelectorAll(selectors.featuredCarousel).forEach((carousel) => {
    const track = carousel.querySelector('[data-carousel-track]');
    const section = carousel.closest('.featured-collection');
    const previous = section?.querySelector('[data-carousel-prev]');
    const next = section?.querySelector('[data-carousel-next]');
    if (!track) return;

    const scrollAmount = () => Math.max(260, carousel.clientWidth * 0.75);

    previous?.addEventListener('click', () => {
      carousel.classList.add('is-manual');
      track.scrollBy({ left: -scrollAmount(), behavior: 'smooth' });
    });

    next?.addEventListener('click', () => {
      carousel.classList.add('is-manual');
      track.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
    });
  });
}

function setupTabbedProductRows() {
  document.querySelectorAll(selectors.tabbedProductRow).forEach((section) => {
    const track = section.querySelector('[data-tabbed-product-track]');
    const previous = section.querySelector('[data-tabbed-product-prev]');
    const next = section.querySelector('[data-tabbed-product-next]');
    if (!track) return;

    const scrollAmount = () => {
      const card = track.querySelector('.editorial-product-card');
      return card ? card.getBoundingClientRect().width + 16 : track.clientWidth * 0.78;
    };

    previous?.addEventListener('click', () => {
      track.scrollBy({ left: -scrollAmount(), behavior: 'smooth' });
    });

    next?.addEventListener('click', () => {
      track.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
    });

  });
}

function setupProductBundleCarousels() {
  const mobileQuery = window.matchMedia('(max-width: 989px)');

  document.querySelectorAll(selectors.productBundleCarousel).forEach((carousel) => {
    if (carousel.dataset.bundleCarouselReady === 'true') return;
    carousel.dataset.bundleCarouselReady = 'true';

    const cards = Array.from(carousel.querySelectorAll('.bundle-product-card'));
    if (cards.length < 2) return;

    let timer = null;
    let isInteracting = false;

    const cardStep = () => {
      const first = cards[0];
      const second = cards[1];
      if (!first || !second) return carousel.clientWidth;
      return Math.max(1, second.offsetLeft - first.offsetLeft);
    };

    const activeIndex = () => {
      const step = cardStep();
      return Math.round(carousel.scrollLeft / step);
    };

    const rotate = () => {
      if (!mobileQuery.matches || isInteracting) return;
      const nextIndex = (activeIndex() + 1) % cards.length;
      carousel.scrollTo({ left: cards[nextIndex].offsetLeft, behavior: 'smooth' });
    };

    const start = () => {
      window.clearInterval(timer);
      if (mobileQuery.matches) timer = window.setInterval(rotate, 3400);
    };

    const pause = () => {
      isInteracting = true;
      window.clearTimeout(carousel.bundleCarouselResumeTimer);
      carousel.bundleCarouselResumeTimer = window.setTimeout(() => {
        isInteracting = false;
      }, 2400);
    };

    carousel.addEventListener('pointerdown', pause);
    carousel.addEventListener('touchstart', pause, { passive: true });
    carousel.addEventListener('wheel', pause, { passive: true });
    mobileQuery.addEventListener?.('change', start);
    start();
  });
}

function setupScrollRevealBanners() {
  const banners = Array.from(document.querySelectorAll(selectors.scrollRevealBanner));
  if (!banners.length) return;

  const update = () => {
    banners.forEach((banner) => {
      const sticky = banner.querySelector('.scroll-reveal-banner__sticky');
      if (!sticky) return;

      const rect = banner.getBoundingClientRect();
      const scrollable = Math.max(1, rect.height - sticky.offsetHeight);
      const startOffset = Math.max(0, window.innerHeight - sticky.offsetHeight);
      const finishOffset = scrollable * 0.82;
      const rawProgress = Math.min(Math.max((startOffset - rect.top) / Math.max(1, startOffset + finishOffset), 0), 1);
      const progress = rawProgress * rawProgress * (3 - (2 * rawProgress));
      const styles = window.getComputedStyle(banner);
      const startX = parseFloat(styles.getPropertyValue('--scroll-reveal-start-x')) || 50;
      const endX = parseFloat(styles.getPropertyValue('--scroll-reveal-end-x')) || startX;
      const startY = parseFloat(styles.getPropertyValue('--scroll-reveal-start-y')) || 0;
      const endY = parseFloat(styles.getPropertyValue('--scroll-reveal-end-y')) || 100;
      const mobileStartY = parseFloat(styles.getPropertyValue('--scroll-reveal-mobile-start-y')) || startY;
      const mobileEndY = parseFloat(styles.getPropertyValue('--scroll-reveal-mobile-end-y')) || endY;

      banner.style.setProperty('--scroll-reveal-progress', progress.toFixed(4));
      banner.style.setProperty('--scroll-reveal-current-x', `${startX + ((endX - startX) * progress)}%`);
      banner.style.setProperty('--scroll-reveal-current-y', `${startY + ((endY - startY) * progress)}%`);
      banner.style.setProperty('--scroll-reveal-current-mobile-y', `${mobileStartY + ((mobileEndY - mobileStartY) * progress)}%`);
    });
  };

  if (window.themeScrollRevealReady !== true) {
    let ticking = false;
    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        setupScrollRevealBanners.update?.();
      });
    };

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    window.themeScrollRevealReady = true;
  }

  setupScrollRevealBanners.update = update;
  update();
}

function setupStickyProductGalleries() {
  const sections = Array.from(document.querySelectorAll('.product--sticky-gallery-desktop'));
  const desktopQuery = window.matchMedia('(min-width: 990px)');
  const stickyTop = 20;

  const resetGallery = (gallery) => {
    if (!gallery) return;
    gallery.style.transform = '';
  };

  const update = () => {
    sections.forEach((section) => {
      const grid = section.querySelector('.product__grid');
      const gallery = section.querySelector('[data-product-gallery]');

      if (!grid || !gallery || !desktopQuery.matches) {
        resetGallery(gallery);
        return;
      }

      const maxTravel = Math.max(0, grid.offsetHeight - gallery.offsetHeight);
      if (maxTravel < 2) {
        resetGallery(gallery);
        return;
      }

      const gridTop = grid.getBoundingClientRect().top;
      const distance = Math.min(Math.max(stickyTop - gridTop, 0), maxTravel);
      gallery.style.transform = distance > 0 ? `translate3d(0, ${Math.round(distance)}px, 0)` : '';
    });
  };

  sections.forEach((section) => {
    section.querySelectorAll('img').forEach((image) => {
      if (image.dataset.stickyGalleryImageReady === 'true') return;
      image.dataset.stickyGalleryImageReady = 'true';
      image.addEventListener('load', () => setupStickyProductGalleries.update?.(), { once: true });
    });
  });

  if (window.themeStickyProductGalleryReady !== true) {
    let ticking = false;
    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        setupStickyProductGalleries.update?.();
      });
    };

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    desktopQuery.addEventListener?.('change', requestUpdate);
    window.themeStickyProductGalleryReady = true;
  }

  setupStickyProductGalleries.update = update;
  update();
}

function initializeTheme() {
  rotateItems(selectors.announcement, '[data-announcement-slide]', 5000);
  rotateItems(selectors.testimonials, '[data-testimonial-slide]', 6500);
  rotateItems(selectors.instagramGallery, '[data-instagram-slide]', 5000);
  setupMobileMenu();
  setupCartDrawer();
  setupWishlist();
  setupCartDiscounts();
  setupCartQuantityLinks();
  setupNewsletterPopup();
  setupSalesPopup();
  setupProductForms();
  setupProductGalleries();
  setupQuantitySteppers();
  setupShareButtons();
  setupCopyCodeButtons();
  setupPromptBackedFields();
  setupNewsletterSafeFields();
  setupAltBrowserFormSubmissions();
  setupPasswordSafeFields();
  setupDesktopOnlyVideos();
  setupHalfCountdownVideos();
  setupCountdownTimers();
  setupIgCarousels();
  setupReviewForms();
  setupFeaturedCarousels();
  setupTabbedProductRows();
  setupProductBundleCarousels();
  setupScrollRevealBanners();
  setupStickyProductGalleries();
  setupCollectionFilters();
  setupSortSelects();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeTheme, { once: true });
} else {
  initializeTheme();
}

document.addEventListener('shopify:section:load', () => {
  setupDesktopOnlyVideos();
  setupHalfCountdownVideos();
  setupIgCarousels();
  setupProductBundleCarousels();
  setupScrollRevealBanners();
  setupStickyProductGalleries();
});
